import { ChangeDetectorRef, Component, Input, Output, OnDestroy, OnInit, ViewChild, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption
} from "@angular/material/autocomplete";
import { MatError, MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { NgForOf, NgIf } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { TalentHappyPathDataService } from "../../service/talent-happy-path-data-service";
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { getCountryIdFromShortCode } from "../../../../../util";
import { Countries } from "../../../../enums";

@Component({
  selector: 'app-location-picker',
  imports: [
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatError,
    MatFormField,
    MatInput,
    MatOption,
    MatProgressSpinner,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    MatLabel,
  ],
  templateUrl: './location-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './location-picker.component.scss'
})
export class LocationPickerComponent implements OnInit, OnDestroy {

  @Output() countryIdChanged = new EventEmitter<number>();
  @Input() formGroup: FormGroup;
  @Input() talent: Talent;
  @Input() required = true;
  @ViewChild('locationAutocompleteTrigger', { read: MatAutocompleteTrigger }) autocompleteTrigger: MatAutocompleteTrigger;
  SEARCH_DEBOUNCE_TIME = 700;

  geoLocation: GeoLocation;
  homeLocation: string;
  locationSuggestions: google.maps.places.AutocompleteSuggestion[] = [];
  isLoadingLocation = false;
  locationValid: boolean = false;
  private sessionToken: google.maps.places.AutocompleteSessionToken;
  private isGoogleMapsLoaded = false;
  private destroy$ = new Subject<void>();
  private googleMapsCheck: ReturnType<typeof setInterval>;
  private googleMapsCheckTimeout: ReturnType<typeof setTimeout>;

  constructor(private cdr: ChangeDetectorRef,
              protected talentHappyPathDataService: TalentHappyPathDataService) {
  }

  /**
   * True when the relevant market (the order's country for a quick apply, or the
   * talent's selected country for a general application) is outside the US/Canada.
   * US/CAN keep the existing zip/postal-code wording; everywhere else uses the
   * broader street/city/postal-code wording.
   */
  get isOutsideNorthAmerica(): boolean {
    const countryId = this.talentHappyPathDataService.homeMarketCountryId;
    return countryId != null && countryId !== Countries.USA && countryId !== Countries.CA;
  }

  /**
   * Placeholder shown inside the input. General application has its own wording;
   * orders outside the US/CAN ask for street/city/postal code; US/CAN orders keep
   * the existing zip/postal-code wording.
   */
  get locationPlaceholderKey(): string {
    if (this.talentHappyPathDataService.isGeneralApplication) {
      return 'talent-happy-path.quick-apply.submit-page.homeLocation-placeholder-general-application';
    }
    if (this.isOutsideNorthAmerica) {
      return 'talent-happy-path.quick-apply.submit-page.homeLocation-placeholder-non-North-America';
    }
    return 'talent-happy-path.quick-apply.submit-page.homeLocation-placeholder';
  }

  /**
   * Required-field error text. General application or any order outside the US/CAN
   * shows the street/city/postal-code message; US/CAN orders keep the zip message.
   */
  get locationRequiredErrorKey(): string {
    return (this.talentHappyPathDataService.isGeneralApplication || this.isOutsideNorthAmerica)
      ? 'talent-happy-path.quick-apply.submit-page.homeLocation-required-location'
      : 'talent-happy-path.quick-apply.submit-page.homeLocation-required-zip';
  }

  /**
   * Error text when a value is typed but no autocomplete suggestion is selected.
   * For a general application or an order outside the US/CAN we show the same
   * street/city/postal-code message as the required error; US/CAN keep the
   * existing "select from the suggestions" prompt.
   */
  get invalidPlaceErrorKey(): string {
    return (this.talentHappyPathDataService.isGeneralApplication || this.isOutsideNorthAmerica)
      ? 'talent-happy-path.quick-apply.submit-page.homeLocation-required-location'
      : 'talent-happy-path.quick-apply.error.invalid-location';
  }

ngOnInit() {
  this.formGroup.addControl('homeLocation',
    new FormControl('', this.required
        ? [Validators.required, this.placeValidator()]
        : [this.placeValidator()]
    )
  );
  this.formGroup.addControl('geoLocation',
    new FormControl('')
  );
  this.formGroup.addControl('countryId',
    new FormControl('')
  );
  // Raw ISO 3166-1 alpha-2 code of the selected place's country. Kept separate from countryId because the
  // Countries enum only covers countries we do business in, so getCountryIdFromShortCode() returns undefined
  // for exactly the home locations we need to detect (India, Singapore, ...) — see BIZ-40245.
  this.formGroup.addControl('homeLocationCountryCode',
    new FormControl('')
  );
  this.initializeGooglePlacesAutocomplete();
}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    clearInterval(this.googleMapsCheck);
    clearTimeout(this.googleMapsCheckTimeout);

    this.formGroup.removeControl('homeLocation');
    this.formGroup.removeControl('geoLocation');
    this.formGroup.removeControl('countryId');
  }

  private placeValidator() {
    return (control: FormControl): ValidationErrors | null => {
      const value = control.value;

      // An empty value is handled by Validators.required — don't also flag
      // invalidPlace, otherwise both error messages render at the same time.
      if (value == null || (typeof value === 'string' && value.trim() === '')) {
        return null;
      }

      // If there's no geoLocation and the value is just text (no selection made)
      if ((!this.geoLocation || !this.geoLocation.latitude) && typeof value === 'string') {
        return { invalidPlace: true };
      }

      return null;
    };
  }

  /**
   * Converts whatever is in the control (an AutocompleteSuggestion object
   * right after a click, or the final address string after we patch it)
   * into the text that appears in the input.
   */
  displaySuggestion(
    value: google.maps.places.AutocompleteSuggestion | string | null
  ): string {
    if (!value) {
      return '';
    }

    // After we patch the control with the plain string, just echo it.
    if (typeof value === 'string') {
      return value;
    }

    // While the control still holds the Suggestion object, show its main text.
    return value.placePrediction?.text?.text ?? '';
  }

  /**
   * Handle place selection from the autocomplete dropdown.
   * This fetches detailed place information and updates the form.
   */
  async onPlaceSelected(event: MatAutocompleteSelectedEvent): Promise<void> {
    const suggestion: google.maps.places.AutocompleteSuggestion = event.option.value;

    if (!suggestion?.placePrediction?.placeId) {
      return;
    }

    try {
      // Create a new Place instance to fetch detailed information
      const place = new google.maps.places.Place({
        id: suggestion.placePrediction.placeId
      });

      // Fetch only the fields we need
      await place.fetchFields({
        fields: ['id', 'displayName', 'formattedAddress', 'location', 'addressComponents']
      });

      // Get the place data
      const placeData = place.toJSON() as any;

      const addressComponents: google.maps.places.AddressComponent[] = placeData.addressComponents;

      if (addressComponents) {
        const countryComponent = addressComponents.find(component =>
          component.types.includes('country')
        );
        let countryShortCode = countryComponent?.shortText;
        // Set the raw code on the form group rather than emitting it: the quick-apply picker instance does not
        // bind (countryIdChanged), so an output alone would never reach the apply payload.
        this.formGroup.controls['homeLocationCountryCode']
          .setValue(countryShortCode ?? null, { emitEvent: false });
        let countryId = getCountryIdFromShortCode(countryShortCode);
        this.formGroup.controls['countryId']
          .setValue(countryId, { emitEvent: false });
        this.countryIdChanged.emit(countryId);
      }

      if (placeData && placeData.id) {
        // Extract location coordinates
        this.geoLocation = {
          latitude: placeData.location?.lat || 0,
          longitude: placeData.location?.lng || 0
        };

        // Set the formatted address
        this.homeLocation = placeData.formattedAddress || placeData.displayName || '';

        // Update the form control but do NOT emit a new valueChanges event
        this.formGroup.controls['homeLocation']
          .setValue(this.homeLocation, { emitEvent: false });

        this.formGroup.controls['geoLocation']
          .setValue(this.geoLocation, { emitEvent: false });

        // Mark the field dirty / touched so validators behave
        this.formGroup.controls['homeLocation'].markAsDirty();
        this.formGroup.controls['homeLocation'].markAsTouched();

        // (Optional) close the panel so it doesn’t reopen
        this.autocompleteTrigger.closePanel();

        // Mark as valid and touched
        this.formGroup.controls['homeLocation'].setErrors(null);

        // Update talent tracking
        this.talent.locationUpdated = true;
        this.locationValid = true;

        // Clear suggestions
        this.locationSuggestions = [];

        // Trigger change detection
        this.cdr.detectChanges();

        // Create a new session token for the next search
        this.createNewSessionToken();
      }
    } catch (error) {
      console.error('Failed to get place details:', error);
      this.formGroup.controls['homeLocation'].setErrors({ placeDetailsError: true });
    }
  }
  /**
   * Create a new session token for Google Maps billing.
   * Each session groups multiple autocomplete requests into one billable unit.
   */
  private createNewSessionToken(): void {
    this.sessionToken = new google.maps.places.AutocompleteSessionToken();
  }

  /**
   * Get the main text from a suggestion for display.
   */
  getSuggestionMainText(suggestion: google.maps.places.AutocompleteSuggestion): string {
    return suggestion?.placePrediction?.text?.text || '';
  }

  /**
   * Get the secondary text (like city, state, country) from a suggestion.
   */
  getSuggestionSecondaryText(suggestion: google.maps.places.AutocompleteSuggestion): string {
    const fullText = suggestion?.placePrediction?.text?.text || '';
    const commaIndex = fullText.indexOf(',');
    return commaIndex > -1 ? fullText.substring(commaIndex + 1).trim() : '';
  }

  /**
   * Initialize Google Places autocomplete functionality.
   * This replaces the old Autocomplete widget with the new API approach.
   */
  private initializeGooglePlacesAutocomplete(): void {
    // Check if Google Maps is already loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.isGoogleMapsLoaded = true;
      this.setupAutocomplete();
    } else {
      // Wait for Google Maps to load
      this.waitForGoogleMaps();
    }
  }

  /**
   * Set up the autocomplete functionality once Google Maps is loaded.
   * This creates the session token and sets up the reactive search pipeline.
   */
  private setupAutocomplete(): void {
    // Create a new session token for billing efficiency
    this.createNewSessionToken();

    // Set up the reactive pipeline for handling user input
    this.setupAutocompletePipeline();
    this.googleMapsReadyResolver();
  }

  /**
   * Wait for Google Maps API to be available.
   * This is necessary because the script might still be loading when our component initializes.
   */
  private waitForGoogleMaps(): void {
    this.googleMapsCheck = setInterval(() => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        clearInterval(this.googleMapsCheck);
        this.isGoogleMapsLoaded = true;
        this.setupAutocomplete();
      }
    }, 100);

    // Timeout after 10 seconds to prevent infinite checking
    this.googleMapsCheckTimeout = setTimeout(() => {
      clearInterval(this.googleMapsCheck);
      if (!this.isGoogleMapsLoaded) {
        console.error('Google Maps failed to load');
      }
    }, 10000);
  }

  /**
   * Set up the reactive pipeline for autocomplete search.
   * This handles debouncing, filtering, and API calls.
   */
  private setupAutocompletePipeline(): void {
    this.formGroup.get('homeLocation').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        // Wait 600ms after user stops typing
        debounceTime(600),
        // Only emit if the value actually changed
        distinctUntilChanged(),
        // Filter out short values and handle clearing
        filter(value => {
          if (!value || value.length < 4) {
            this.locationSuggestions = [];
            this.isLoadingLocation = false;
            this.locationValid = false;
            this.geoLocation = null;
            return false;
          }
          return true;
        }),
        // Cancel previous requests and make new API call
        switchMap(value => {
          this.isLoadingLocation = true;
          return this.searchPlaces(value);
        })
      )
      .subscribe({
        next: suggestions => {
          this.locationSuggestions = suggestions;
          this.isLoadingLocation = false;
        },
        error: error => {
          console.error('Autocomplete error:', error);
          this.locationSuggestions = [];
          this.isLoadingLocation = false;
        }
      });
  }

  /**
   * Search for place suggestions using the new Google Maps API.
   * This uses AutocompleteSuggestion.fetchAutocompleteSuggestions() instead of the old Autocomplete widget.
   */
  private async searchPlaces(query: string): Promise<google.maps.places.AutocompleteSuggestion[]> {
    try {
      const request = {
        input: query,
        sessionToken: this.sessionToken,
        // Include country restrictions if specified !!!!!! NO we want to allow for all countries
        // includedRegionCodes: this.allowedCountries.map(c => c.toUpperCase())
      };

      // Use the new static method from Google's API
      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      return suggestions || [];
    } catch (error) {
      console.error('Failed to fetch autocomplete suggestions:', error);
      return [];
    }
  }

  /**
   * Programmatically set a location value, mimicking user input and selection.
   * This will trigger the autocomplete search and auto-select the first matching result.
   */
  async setLocationValue(locationText: string): Promise<void> {
    await this.googleMapsReady;
    if (!locationText || !this.isGoogleMapsLoaded) {
      return;
    }

    try {
      // Create a promise that resolves when suggestions are available
      const suggestionsReady = new Promise<void>((resolve) => {
        const subscription = this.formGroup.get('homeLocation').valueChanges
            .pipe(
                debounceTime(this.SEARCH_DEBOUNCE_TIME),
                filter(value => value === locationText),
                switchMap(() => this.searchPlaces(locationText))
            )
            .subscribe({
              next: (suggestions) => {
                this.locationSuggestions = suggestions;
                subscription.unsubscribe();
                resolve();
              },
              error: (error) => {
                console.error('Search failed:', error);
                subscription.unsubscribe();
                resolve();
              }
            });
      });

      // Set the value to trigger the search
      this.formGroup.controls['homeLocation'].setValue(locationText);

      // Wait for the actual search to complete
      await suggestionsReady;

      // Auto-select first suggestion if available
      if (this.locationSuggestions.length > 0) {
        const mockEvent = {
          option: { value: this.locationSuggestions[0] }
        } as MatAutocompleteSelectedEvent;

        await this.onPlaceSelected(mockEvent);
      }
    } catch (error) {
      console.error('Failed to set location value programmatically:', error);
    }
  }

  private googleMapsReadyResolver: () => void;
  private googleMapsReady: Promise<void> = new Promise(resolve => {
    this.googleMapsReadyResolver = resolve;
  });

}
